import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SessionTelemetryCategory } from '../../graphql/__generated__/graphql';
import type { CanvassingSessionTelemetryQuery } from '../../graphql/queries/canvassingSessionTelemetry.generated';
import {
  CANVASSING_SESSION_TELEMETRY_PAGE_SIZE_OPTIONS,
  fetchCanvassingSessionTelemetry,
  toCanvassingSessionTelemetryRequestError,
  type CanvassingSessionFetchImpl,
  type CanvassingSessionTelemetryPageSize,
  type CanvassingSessionTelemetryRequestError,
} from './canvassingSessionApi';
import { isCanvassingSessionId } from './useCanvassingSessionDetail';

export const CANVASSING_SESSION_TELEMETRY_POLL_INTERVAL_MS = 5_000;

export type CanvassingSessionTelemetryPublication = NonNullable<CanvassingSessionTelemetryQuery['canvassingSessionTelemetry']>;

export type CanvassingSessionTelemetryControls = {
  category: SessionTelemetryCategory | undefined;
  failuresOnly: boolean;
  page: number;
  rowsPerPage: CanvassingSessionTelemetryPageSize;
};

export type CanvassingSessionTelemetryState =
  | { status: 'idle' }
  | { status: 'loading'; manualRefreshing: boolean }
  | { status: 'unavailable' }
  | { status: 'error'; error: CanvassingSessionTelemetryRequestError }
  | {
      status: 'ready';
      publication: CanvassingSessionTelemetryPublication;
      refreshing: boolean;
      manualRefreshing: boolean;
      backgroundError: CanvassingSessionTelemetryRequestError | null;
    };

export type CanvassingSessionTelemetryRequest = {
  sessionId: string;
  first: CanvassingSessionTelemetryPageSize;
  offset: number;
  snapshot?: string;
  category?: SessionTelemetryCategory;
  failuresOnly: boolean;
};

export type CanvassingSessionTelemetryApi = {
  fetchTelemetry: (
    request: CanvassingSessionTelemetryRequest,
    options: { signal: AbortSignal },
  ) => Promise<CanvassingSessionTelemetryQuery['canvassingSessionTelemetry']>;
};

export type UseCanvassingSessionTelemetryOptions = {
  sessionId: string | undefined;
  pollingRecommended: boolean;
  fetchImpl?: CanvassingSessionFetchImpl;
  api?: CanvassingSessionTelemetryApi;
};

export type UseCanvassingSessionTelemetryResult = {
  controls: CanvassingSessionTelemetryControls;
  state: CanvassingSessionTelemetryState;
  setCategory: (category: SessionTelemetryCategory | undefined) => void;
  setFailuresOnly: (failuresOnly: boolean) => void;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  reset: () => void;
  refresh: () => void;
  retry: () => void;
};

type RequestKind = 'initial' | 'automatic' | 'manual' | 'pagination' | 'filter' | 'reset' | 'retry';

type LifecycleActions = {
  setCategory: (category: SessionTelemetryCategory | undefined) => void;
  setFailuresOnly: (failuresOnly: boolean) => void;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  reset: () => void;
  refresh: () => void;
  retry: () => void;
  updatePollingRecommendation: () => void;
};

const DEFAULT_CONTROLS: CanvassingSessionTelemetryControls = {
  category: undefined,
  failuresOnly: false,
  page: 0,
  rowsPerPage: 25,
};

const telemetryApiFromFetch = (fetchImpl: CanvassingSessionFetchImpl | undefined): CanvassingSessionTelemetryApi => ({
  fetchTelemetry: async (request, { signal }) => {
    const response = await fetchCanvassingSessionTelemetry({ ...request, signal, fetchImpl });
    return response.canvassingSessionTelemetry;
  },
});

const isTelemetryPageSize = (value: number): value is CanvassingSessionTelemetryPageSize =>
  CANVASSING_SESSION_TELEMETRY_PAGE_SIZE_OPTIONS.some(option => option === value);

const normalizePage = (page: number): number => Number.isSafeInteger(page) && page >= 0 ? page : 0;

export const useCanvassingSessionTelemetry = ({
  sessionId,
  pollingRecommended,
  fetchImpl,
  api: apiOverride,
}: UseCanvassingSessionTelemetryOptions): UseCanvassingSessionTelemetryResult => {
  const api = useMemo(
    () => apiOverride ?? telemetryApiFromFetch(fetchImpl),
    [apiOverride?.fetchTelemetry, fetchImpl],
  );
  const [controls, setControls] = useState<CanvassingSessionTelemetryControls>(DEFAULT_CONTROLS);
  const [state, setState] = useState<CanvassingSessionTelemetryState>({ status: 'idle' });
  const pollingRecommendedRef = useRef(pollingRecommended);
  const actionsRef = useRef<LifecycleActions>({
    setCategory: () => undefined,
    setFailuresOnly: () => undefined,
    setPage: () => undefined,
    setRowsPerPage: () => undefined,
    reset: () => undefined,
    refresh: () => undefined,
    retry: () => undefined,
    updatePollingRecommendation: () => undefined,
  });

  pollingRecommendedRef.current = pollingRecommended;

  const setCategory = useCallback((category: SessionTelemetryCategory | undefined) => actionsRef.current.setCategory(category), []);
  const setFailuresOnly = useCallback((failuresOnly: boolean) => actionsRef.current.setFailuresOnly(failuresOnly), []);
  const setPage = useCallback((page: number) => actionsRef.current.setPage(page), []);
  const setRowsPerPage = useCallback((rowsPerPage: number) => actionsRef.current.setRowsPerPage(rowsPerPage), []);
  const reset = useCallback(() => actionsRef.current.reset(), []);
  const refresh = useCallback(() => actionsRef.current.refresh(), []);
  const retry = useCallback(() => actionsRef.current.retry(), []);

  useEffect(() => {
    if (!isCanvassingSessionId(sessionId)) {
      setControls(DEFAULT_CONTROLS);
      setState({ status: 'idle' });
      return undefined;
    }

    let active = true;
    let controlsForRequest = DEFAULT_CONTROLS;
    let publication: CanvassingSessionTelemetryPublication | null = null;
    let activeController: AbortController | null = null;
    let requestGeneration = 0;
    let inFlight = false;
    let automaticPollingEnabled = true;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const clearPollTimer = (): void => {
      if (pollTimer === null) return;
      clearTimeout(pollTimer);
      pollTimer = null;
    };

    const abortActiveRequest = (): void => {
      requestGeneration += 1;
      inFlight = false;
      const controller = activeController;
      activeController = null;
      controller?.abort();
    };

    const requestIsCurrent = (controller: AbortController, generation: number): boolean =>
      active && !controller.signal.aborted && generation === requestGeneration && activeController === controller;

    const schedulePoll = (): void => {
      clearPollTimer();
      if (
        !active
        || publication === null
        || controlsForRequest.page !== 0
        || !pollingRecommendedRef.current
        || !automaticPollingEnabled
        || inFlight
      ) {
        return;
      }

      pollTimer = setTimeout(() => {
        pollTimer = null;
        startRequest('automatic');
      }, CANVASSING_SESSION_TELEMETRY_POLL_INTERVAL_MS);
    };

    const startRequest = (kind: RequestKind, replaceInFlight = false): void => {
      if (!active) return;
      if (inFlight) {
        if (!replaceInFlight) return;
        abortActiveRequest();
      }

      clearPollTimer();
      const controller = new AbortController();
      const generation = ++requestGeneration;
      activeController = controller;
      inFlight = true;

      const isManual = kind === 'manual' || kind === 'retry';
      if (publication === null) {
        setState({ status: 'loading', manualRefreshing: isManual });
      } else {
        setState({
          status: 'ready',
          publication,
          refreshing: true,
          manualRefreshing: isManual,
          backgroundError: null,
        });
      }

      const obtainsFreshSnapshot = controlsForRequest.page === 0
        && (kind === 'initial' || kind === 'automatic' || kind === 'manual' || kind === 'filter' || kind === 'reset' || kind === 'retry');
      const request: CanvassingSessionTelemetryRequest = {
        sessionId,
        first: controlsForRequest.rowsPerPage,
        offset: controlsForRequest.page * controlsForRequest.rowsPerPage,
        failuresOnly: controlsForRequest.failuresOnly,
        ...(obtainsFreshSnapshot || publication === null ? {} : { snapshot: publication.snapshot }),
        ...(controlsForRequest.category === undefined ? {} : { category: controlsForRequest.category }),
      };

      void api.fetchTelemetry(request, { signal: controller.signal }).then(result => {
        if (!requestIsCurrent(controller, generation)) return;

        activeController = null;
        inFlight = false;
        if (result === null) {
          publication = null;
          setState({ status: 'unavailable' });
          return;
        }

        publication = result;
        if (kind === 'manual' || kind === 'retry') automaticPollingEnabled = true;
        setState({
          status: 'ready',
          publication: result,
          refreshing: false,
          manualRefreshing: false,
          backgroundError: null,
        });
        schedulePoll();
      }).catch(error => {
        if (!requestIsCurrent(controller, generation)) return;

        activeController = null;
        inFlight = false;
        const requestError = toCanvassingSessionTelemetryRequestError(error);
        if (publication === null) {
          setState({ status: 'error', error: requestError });
        } else {
          setState({
            status: 'ready',
            publication,
            refreshing: false,
            manualRefreshing: false,
            backgroundError: requestError,
          });
        }

        if (kind === 'automatic') automaticPollingEnabled = false;
      });
    };

    const resetToFirstPage = (nextControls: Omit<CanvassingSessionTelemetryControls, 'page'>, kind: 'filter' | 'reset'): void => {
      controlsForRequest = { ...nextControls, page: 0 };
      setControls(controlsForRequest);
      startRequest(kind, true);
    };

    actionsRef.current = {
      setCategory: category => {
        if (category === controlsForRequest.category) return;
        resetToFirstPage({ ...controlsForRequest, category }, 'filter');
      },
      setFailuresOnly: failuresOnly => {
        if (failuresOnly === controlsForRequest.failuresOnly) return;
        resetToFirstPage({ ...controlsForRequest, failuresOnly }, 'filter');
      },
      setPage: page => {
        const nextPage = normalizePage(page);
        if (nextPage === controlsForRequest.page) return;
        controlsForRequest = { ...controlsForRequest, page: nextPage };
        setControls(controlsForRequest);
        startRequest('pagination', true);
      },
      setRowsPerPage: rowsPerPage => {
        if (!isTelemetryPageSize(rowsPerPage) || rowsPerPage === controlsForRequest.rowsPerPage) return;
        resetToFirstPage({ ...controlsForRequest, rowsPerPage }, 'filter');
      },
      reset: () => resetToFirstPage({ ...DEFAULT_CONTROLS }, 'reset'),
      refresh: () => startRequest('manual', true),
      retry: () => startRequest('retry', true),
      updatePollingRecommendation: () => {
        if (!pollingRecommendedRef.current) {
          clearPollTimer();
          return;
        }
        schedulePoll();
      },
    };

    setControls(DEFAULT_CONTROLS);
    setState({ status: 'loading', manualRefreshing: false });
    startRequest('initial');

    return () => {
      active = false;
      actionsRef.current = {
        setCategory: () => undefined,
        setFailuresOnly: () => undefined,
        setPage: () => undefined,
        setRowsPerPage: () => undefined,
        reset: () => undefined,
        refresh: () => undefined,
        retry: () => undefined,
        updatePollingRecommendation: () => undefined,
      };
      clearPollTimer();
      abortActiveRequest();
    };
  }, [api, sessionId]);

  useEffect(() => {
    actionsRef.current.updatePollingRecommendation();
  }, [pollingRecommended]);

  return {
    controls,
    state,
    setCategory,
    setFailuresOnly,
    setPage,
    setRowsPerPage,
    reset,
    refresh,
    retry,
  };
};
